"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faqaccordion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "Are your pest control services safe for my family and pets?",
    answer: "Yes. At Peace River Pest Control, we prioritize safety. We use EPA-approved products and follow strict application protocols to ensure your home remains a safe environment for your children and pets while effectively eliminating pests."
  }, {
    question: "How do you handle termite infestations?",
    answer: "We provide comprehensive termite inspections and advanced treatment solutions. Our experts identify the type of termite and implement targeted barriers or baiting systems to protect your property's structural integrity.",
    bullets: ["Full property termite inspections", "Liquid barrier treatments", "Advanced baiting systems", "Preventative wood-destroying organism reports"]
  }, {
    question: "How often should I schedule pest control treatments?",
    answer: "For most homes in Southwest Florida, we recommend quarterly maintenance. This consistent schedule creates a protective barrier that prevents pests from establishing themselves, especially during peak breeding seasons."
  }, {
    question: "How is the price for my pest control service calculated?",
    answer: "Pricing is based on the size of your property, the type of pests, and the specific treatment plan required. We provide transparent, upfront quotes with no hidden fees, ensuring you know exactly what to expect before we start."
  }, {
    question: "Do you offer specialized mosquito control?",
    answer: "Absolutely. Our mosquito control program targets breeding sites and resting areas around your yard. We offer recurring misting services to keep your outdoor living spaces comfortable and bite-free throughout the year."
  }, {
    question: "What areas do you serve in Florida?",
    answer: "Peace River Pest Control proudly serves Punta Gorda, Port Charlotte, Cape Coral, Burnt Store, and North Fort Myers. We are your local experts dedicated to keeping our community pest-free."
  }, {
    question: "Can you handle emergency pest situations?",
    answer: "Yes. We understand that some pest issues require immediate attention. We offer flexible scheduling to address urgent infestations quickly, helping you regain peace of mind in your home."
  }, {
    question: "What if I see pests between scheduled visits?",
    answer: "We stand by our work. If you notice pest activity between your regular service appointments, just give us a call. We will return to your property to re-treat the affected areas at no additional cost."
  }, {
    question: "Do you treat for spiders and other common household pests?",
    answer: "Yes. Our general pest control covers a wide range of common invaders, including ants, roaches, spiders, millipedes, and silverfish. We focus on both interior protection and exterior perimeter defense."
  }, {
    question: "How do I get a free inspection?",
    answer: "Getting started is easy. You can request a free inspection through our website or call our office directly. Our team will assess your property and provide a customized plan tailored to your specific needs."
  }];
  return <section data-section-id="701" id="faqaccordion" className="bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <span className="inline-block text-sm font-semibold uppercase tracking-widest text-secondary mb-6">
                  FAQ
                </span>
                <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-bold leading-[1.05] tracking-tight text-foreground mb-6">
                  Common Pest Control Questions
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md">
                  Everything you need to know about our professional pest, termite, and mosquito control services in Southwest Florida.
                </p>

                {}
                <div className="bg-card rounded-lg p-6 shadow-md border border-border mb-6">
                  <div className="flex items-start gap-4">
                    <SafeHydrate><Avatar className="size-12 shrink-0">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/pest_expert_avatar.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/pest_expert_avatar.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/pest_expert_avatar.png-wvc-sizes" alt="Peace River Pest Control expert" />
                      <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold">
                        PR
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="flex-1">
                      <p className="font-default font-semibold text-foreground mb-1">
                        Still have questions?
                      </p>
                      <p className="text-sm text-muted-foreground mb-3">
                        Our local team is ready to help you protect your home.
                      </p>
                      <Link to="/ContactPage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-secondary transition-colors group">
                        Contact our experts
                        <ArrowRight className="size-4 text-secondary transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>

                {}
                <Button asChild size="lg" variant="outline" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg">
                  <Link to="tel:(555) 123-4567">
                    <Phone className="size-4 text-secondary" />
                    Call (555) 123-4567
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group/item data-[state=open]:border-l-[3px] data-[state=open]:border-l-secondary data-[state=open]:pl-6 transition-all duration-300">
                  <AccordionTrigger className="py-6 hover:no-underline [&>svg]:hidden group">
                    <span className="font-default text-lg md:text-xl font-semibold text-foreground text-left pr-6 leading-snug">
                      {faq.question}
                    </span>
                    <span aria-hidden="true" className="relative flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-secondary transition-all duration-300 group-hover:bg-secondary group-hover:text-secondary-foreground group-data-[state=open]:bg-secondary group-data-[state=open]:text-secondary-foreground">
                      <span className="absolute h-0.5 w-4 bg-current rounded" />
                      <span className="absolute h-0.5 w-4 bg-current rounded transition-transform duration-300 group-data-[state=open]:rotate-90 group-data-[state=closed]:rotate-0 rotate-90" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pr-12">
                    <p className="text-base text-muted-foreground leading-[1.7]">
                      {faq.answer}
                    </p>
                    {faq.bullets && <ul className="mt-4 space-y-2">
                        {faq.bullets.map((bullet, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-base text-muted-foreground leading-[1.7]">
                            <span className="mt-2.5 size-1.5 shrink-0 rounded-lg bg-secondary" />
                            <span>{bullet}</span>
                          </li>)}
                      </ul>}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}